import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, siteImages, categories } from '../data/mock';
import usePageTitle from '../hooks/usePageTitle';
import { ArrowUpRight, ChevronDown, Grid2X2, Rows3, Sparkles, Heart, ArrowRight } from 'lucide-react';

const ShopPage = () => {
  usePageTitle(
    'Sewing Equipment & Craft Tools',
    'Browse our complete collection of sewing gadgets and DIY craft tools. Rotary cutters, sewing machines, dress forms, and more at Stitch Your Dash.'
  );

  const [sortBy, setSortBy] = useState('featured');
  const [showSort, setShowSort] = useState(false);
  const [view, setView] = useState('grid');

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'name', label: 'Name A-Z' },
  ];

  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sortBy) {
      case 'price-low': return sorted.sort((a, b) => a.price - b.price);
      case 'price-high': return sorted.sort((a, b) => b.price - a.price);
      case 'name': return sorted.sort((a, b) => a.name.localeCompare(b.name));
      default: return sorted.sort((a, b) => (b.badge === 'Best Seller' ? 1 : 0) - (a.badge === 'Best Seller' ? 1 : 0));
    }
  }, [sortBy]);

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Banner Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={siteImages.shopBanner} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 gradient-banner-overlay" />
          </div>
          <div className="relative container-wide py-20 lg:py-28">
            <div className="max-w-3xl text-cream-50">
              <p className="eyebrow text-rose-200 mb-4" style={{color: '#f5d2c6'}}><Sparkles className="w-3.5 h-3.5" /> The Collection</p>
              <h1 className="font-display text-editorial-lg leading-[1.05]">
                Sewing equipment & <em className="italic-display text-rose-200 not-italic" style={{fontStyle:'italic'}}>craft tools</em>.
              </h1>
              <p className="mt-5 max-w-xl text-cream-200/85 text-base lg:text-lg">
                Premium gadgets and DIY essentials, hand-picked for cozy studios and creative makers.
              </p>
              <div className="mt-8 flex flex-wrap gap-3 text-xs uppercase tracking-[0.18em] text-cream-100">
                <span className="px-4 py-2 rounded-full bg-cream-50/15 border border-cream-50/20 backdrop-blur-sm">{products.length} products</span>
                <span className="px-4 py-2 rounded-full bg-cream-50/15 border border-cream-50/20 backdrop-blur-sm">USA shipping</span>
                <span className="px-4 py-2 rounded-full bg-cream-50/15 border border-cream-50/20 backdrop-blur-sm">Made for Makers</span>
              </div>
            </div>
          </div>
        </section>

        {/* Promo strip */}
        <section className="bg-warmBrown-900 text-cream-100 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap py-3 text-sm">
            {Array.from({ length: 2 }).map((_, idx) => (
              <div key={idx} className="flex items-center gap-10 px-5">
                {['Free Shipping on Orders $99+', '30-Day Easy Returns', 'Secure SSL Checkout', 'Quality Guaranteed', 'Curated for Crafters'].map((t, i) => (
                  <React.Fragment key={`${idx}-${i}`}>
                    <span>✨ {t}</span>
                    <span className="text-rose-400">•</span>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* Category chips */}
        <section className="bg-cream-50 border-b border-cream-200">
          <div className="container-wide py-7">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 -mx-2 px-2 lg:flex-wrap lg:overflow-visible">
              <Link to="/shop" className="flex-shrink-0 px-5 py-2.5 rounded-full bg-warmBrown-900 text-cream-50 text-sm font-medium">
                All Products
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to="/shop"
                  className="flex-shrink-0 px-5 py-2.5 rounded-full bg-warmWhite-100 hover:bg-rose-100 hover:text-rose-700 border border-cream-200 text-warmBrown-700 text-sm transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Toolbar */}
        <section className="bg-warmWhite-100 border-b border-cream-200 sticky top-[64px] md:top-[105px] lg:top-[125px] z-30 backdrop-blur-md bg-warmWhite-100/85">
          <div className="container-wide flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4">
            <div className="flex items-center gap-3 text-sm">
              <Link to="/" className="text-warmBrown-500 hover:text-rose-600">Home</Link>
              <span className="text-warmBrown-300">/</span>
              <span className="text-warmBrown-900 font-medium">Shop</span>
              <span className="hidden sm:inline text-warmBrown-300">·</span>
              <span className="hidden sm:inline text-warmBrown-500">{sortedProducts.length} items</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex bg-cream-100 rounded-full p-1">
                <button
                  onClick={() => setView('grid')}
                  className={`p-2 rounded-full transition-all ${view === 'grid' ? 'bg-warmWhite-100 text-rose-600 shadow-soft' : 'text-warmBrown-500 hover:text-warmBrown-800'}`}
                  aria-label="Grid view"
                >
                  <Grid2X2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setView('list')}
                  className={`p-2 rounded-full transition-all ${view === 'list' ? 'bg-warmWhite-100 text-rose-600 shadow-soft' : 'text-warmBrown-500 hover:text-warmBrown-800'}`}
                  aria-label="List view"
                >
                  <Rows3 className="w-4 h-4" />
                </button>
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowSort(!showSort)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-warmWhite-100 rounded-full border border-cream-200 hover:border-rose-300 min-w-[180px] justify-between text-sm shadow-soft"
                >
                  <span className="text-warmBrown-700">Sort: <span className="font-medium text-warmBrown-900">{sortOptions.find(o => o.value === sortBy)?.label}</span></span>
                  <ChevronDown className={`w-4 h-4 text-warmBrown-400 transition-transform ${showSort ? 'rotate-180' : ''}`} />
                </button>
                {showSort && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowSort(false)} />
                    <div className="absolute top-full right-0 mt-2 w-full bg-warmWhite-100 rounded-2xl shadow-elevated border border-cream-200 py-2 z-50">
                      {sortOptions.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => { setSortBy(opt.value); setShowSort(false); }}
                          className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${sortBy === opt.value ? 'bg-rose-50 text-rose-700 font-medium' : 'text-warmBrown-700 hover:bg-cream-50'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Products grid/list */}
        <section className="py-12 lg:py-16">
          <div className="container-wide">
            {view === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {sortedProducts.map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} />
                ))}
              </div>
            ) : (
              <div className="bg-warmWhite-100 rounded-[12px] border-t border-warmBrown-900/10 px-3 lg:px-5">
                {sortedProducts.map((p, idx) => (
                  <ProductCard key={p.id} product={p} index={idx} variant="row" />
                ))}
              </div>
            )}

            {/* Bottom help */}
            <div className="mt-20 grid lg:grid-cols-2 gap-6 lg:gap-10 items-center bg-rose-50 rounded-[36px] border border-rose-100 p-8 lg:p-12">
              <div>
                <Heart className="w-9 h-9 text-rose-500 mb-4" />
                <h3 className="font-display text-2xl lg:text-3xl text-warmBrown-950 mb-3">
                  Need help finding the right tool?
                </h3>
                <p className="text-warmBrown-700 mb-6">
                  Our cozy little support team is here to help you pick the right gadget for your next project. We reply fast — promise.
                </p>
                <Link to="/contact" className="btn-secondary">
                  Contact Us
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="hidden lg:block aspect-[5/3] rounded-[28px] overflow-hidden">
                <img src={siteImages.heroSecondary} alt="Friendly studio" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
